// utils/reloadSearch.ts
export type SearchPayload = {
  page: string;          // 當前 pathname，防跨頁誤觸
  ts: number;            // 時戳，避免舊資料
  filters: any;          // 你的搜尋條件物件
};

export interface ImportFileState {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileContent: string; // base64 encoded
  previewData: any[];
  importType: string;
  timestamp: number;
}

const KEY = 'PENDING_RELOAD_SEARCH';
const FILE_KEY = 'PENDING_IMPORT_FILE';
const EXPIRE_MS = 15_000; // 15 秒內有效

export function triggerReloadSearchWithFile(filters: any, fileState?: ImportFileState) {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters,
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));
  
  // 保存文件状态
  if (fileState) {
    sessionStorage.setItem(FILE_KEY, JSON.stringify(fileState));
  }
  
  window.location.reload(); // 等同 F5
}

// 保持向后兼容的原函数
export function triggerReloadSearch(filters: any) {
  triggerReloadSearchWithFile(filters);
}

// 專門處理日期選擇的重新載入機制
export function triggerReloadSearchWithDate(date: string) {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters: { selectedDate: date, source: 'date_selection' }, // 添加來源標記
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));

  window.location.reload(); // 等同 F5
}

// 專門處理設定儲存的重新載入機制
export function triggerReloadWithSettings(settings: any, source: string = 'settings_save') {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters: { settings, source }, // 保存設定資料和來源標記
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));

  window.location.reload(); // 等同 F5
}

// 專門處理設定儲存完成後的重新整理（不保存任何使用者畫面資料）
export function triggerReloadAfterSave() {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters: { source: 'settings_save_complete' }, // 僅標記為儲存完成，不保存任何設定資料
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));

  window.location.reload(); // 等同 F5
}

// 專門處理到期日核對搜尋的重新載入機制
export function triggerReloadWithExpirySearchFilters(filters: any) {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters: { ...filters, source: 'expiry_search' }, // 保存篩選條件和來源標記
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));

  window.location.reload(); // 等同 F5
}

// 專門處理銷售分析搜尋的重新載入機制
export function triggerReloadWithSalesAnalysisFilters(filters: any) {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters: { ...filters, source: 'sales_analysis_search' }, // 保存篩選條件和來源標記
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));

  window.location.reload(); // 等同 F5
}

// 專門處理流量分析搜尋的重新載入機制
export function triggerReloadWithTrafficAnalysisFilters(filters: any) {
  const payload: SearchPayload = {
    page: window.location.pathname,
    ts: Date.now(),
    filters: { ...filters, source: 'traffic_analysis_search' }, // 保存篩選條件和來源標記
  };
  sessionStorage.setItem(KEY, JSON.stringify(payload));

  window.location.reload(); // 等同 F5
}


export function pickReloadSearch(): SearchPayload | null {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  sessionStorage.removeItem(KEY); // 先移除，避免重複觸發
  try {
    const p = JSON.parse(raw) as SearchPayload;
    const samePage = p.page === window.location.pathname;
    const fresh = Date.now() - p.ts < EXPIRE_MS;
    return samePage && fresh ? p : null;
  } catch { return null; }
}

export function pickImportFile(): ImportFileState | null {
  const raw = sessionStorage.getItem(FILE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(FILE_KEY); // 先移除，避免重複觸發
  try {
    const fileState = JSON.parse(raw) as ImportFileState;
    const fresh = Date.now() - fileState.timestamp < EXPIRE_MS;
    return fresh ? fileState : null;
  } catch { return null; }
}

// 辅助函数：将文件转换为base64
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// 辅助函数：从base64创建文件
export function base64ToFile(base64: string, fileName: string, fileType: string): File {
  const byteCharacters = atob(base64.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], fileName, { type: fileType });
}
